<script setup lang="ts">
import type { ButtonProps } from '/#/components/base/button/button'
import type { ModalEmit, ModalProps } from '/#/components/base/modal'

import merge from 'lodash-es/merge'
import { useI18n } from 'vue-i18n'

const props = withDefaults(defineProps<ModalProps>(), {
  title: undefined,
  size: 'xs',
  headerDescription: undefined,
  headerIcon: undefined,
  showFooter: false,
  hasHeader: true,
  buttonsPosition: 'end',
  cancelButton: false,
  confirmButton: false,
  cancelLabel: undefined,
  removeBodyPadding: false,
})

const emit = defineEmits<ModalEmit>()

const { t } = useI18n()

watch(
  () => props.open,
  (value) => {
    if (value) document.documentElement.classList.add('no-scroll')
    else document.documentElement.classList.remove('no-scroll')
  },
)

const confirmButtonProps: ComputedRef<ButtonProps> = computed(
  () =>
    merge(
      {
        variant: 'solid',
        color: 'tertiary',
        text: t('action.confirm'),
      },
      props.confirmButton,
    ) as ButtonProps,
)

const cancelButtonProps: ComputedRef<ButtonProps> = computed(
  () =>
    merge(
      {
        variant: 'outlined',
        color: 'slate',
        text: t('action.cancel'),
      },
      props.cancelButton,
    ) as ButtonProps,
)
</script>

<template>
  <Teleport v-if="open" to="body">
    <div
      :class="[open && 'is-active', size && `${size}`]"
      class="modal modal-background modal-close"
     
      @keydown.self.esc="emit('close')"
      @click.self.stop="emit('close')"
    >
      <div class="modal-content">
        <div class="modal-card">
          <header v-if="hasHeader" class="modal-card-head">
            <slot name="header">
              <Header
                no-padding
                :title="title"
                :description="headerDescription"
                :icon="headerIcon"
              />
              <button
                type="button"
                class="ml-auto modal-close"
                aria-label="close"
                tabindex="0"
               
                @click="emit('close')"
              >
                <Icon name="close" />
              </button>
            </slot>
          </header>
          <div
            class="modal-card-body"
            :class="{
              'is-bottom-rounded': !showFooter,
              'is-removed-padding': removeBodyPadding,
              'is-top-rounded': !hasHeader,
            }"
          >
            <div class="inner-content">
              <slot name="content" />
            </div>
          </div>
          <div
            v-if="showFooter"
            class="modal-card-foot"
            :class="[
              buttonsPosition === 'center' && 'is-centered',
              buttonsPosition === 'end' && 'is-end',
            ]"
           
          >
            <div class="button-area">
              <Button
                v-if="cancelButton"
                v-bind="cancelButtonProps"
               
                @click="emit('close')"
              />
              <Button
                v-if="confirmButton"
                v-bind="confirmButtonProps"
               
                @click="emit('confirm')"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
